import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Compass, List, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useI18n } from '../../contexts/I18nContext';

// Import modular components
import ForgeHeader from './components/ForgeHeader';
import CourseSelector from './components/CourseSelector';
import DungeonBackground from './components/DungeonBackground';
import CastleTop from './components/CastleTop';
import Torch from './components/Torch';
import LessonConnector from './components/LessonConnector';
import LessonNode from './components/LessonNode';
import ForgeEntrance from './components/ForgeEntrance';
import EmberParticles from './components/EmberParticles';
import LessonModal from './components/LessonModal';
import ForgeOnboarding from './components/ForgeOnboarding';

/**
 * ForgePathPage - Main page component for the Forge Path learning progression
 * 
 * Features:
 * - Medieval dungeon corridor aesthetic
 * - Interactive lesson nodes
 * - Progress tracking
 * - Responsive design
 * - Animated atmosphere (torches, embers)
 * - Onboarding tutorial for new users
 */
export default function ForgePathPage() {
  const navigate = useNavigate();
  const { languageCode } = useParams();
  const [searchParams] = useSearchParams();
  const course = parseInt(searchParams.get('course') || '1');
  const { t } = useI18n();
  
  // State
  const [loading, setLoading] = useState(true);
  const [pathData, setPathData] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [error, setError] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [personalizedMode, setPersonalizedMode] = useState(() => {
    return localStorage.getItem('forge_personalized_mode') === 'true';
  });
  const [personalizedData, setPersonalizedData] = useState(null);

  // Check if user has seen onboarding
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('forge_onboarding_complete');
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    }
  }, []);

  const handleOnboardingComplete = () => {
    localStorage.setItem('forge_onboarding_complete', 'true');
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    localStorage.setItem('forge_onboarding_complete', 'true');
    setShowOnboarding(false);
  };

  // Fetch path data
  useEffect(() => {
    fetchPathData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageCode, course]);

  // Scroll to bottom when path data loads (so user starts at lesson 1)
  useEffect(() => {
    if (pathData && !loading && !showOnboarding) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        const target = document.body.scrollHeight - window.innerHeight;
        const start = window.scrollY;
        const distance = target - start;
        const duration = Math.max(2000, Math.abs(distance) * 1.5); // 50% slower scroll
        let startTime = null;

        function step(timestamp) {
          if (!startTime) startTime = timestamp;
          const elapsed = timestamp - startTime;
          const progress = Math.min(elapsed / duration, 1);
          // Ease-in-out curve
          const ease = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;
          window.scrollTo(0, start + distance * ease);
          if (progress < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      }, 300);
    }
  }, [pathData, loading, showOnboarding]);

  const fetchPathData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pathRes, persRes] = await Promise.all([
        api.get(`/forge/path/${languageCode}/${course}`),
        api.get(`/forge/personalized/${languageCode}/${course}`).catch(() => null)
      ]);
      setPathData(pathRes.data);
      if (persRes?.data?.lessons) {
        setPersonalizedData(persRes.data.lessons);
      }
    } catch (err) {
      console.error('Failed to fetch forge path:', err);
      if (err.response?.status === 403) {
        setError(err.response.data.detail);
      } else {
        toast.error('Failed to load Forge Path');
        setError('Failed to load path data');
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePersonalizedMode = () => {
    setPersonalizedMode(prev => {
      const next = !prev;
      localStorage.setItem('forge_personalized_mode', String(next));
      return next;
    });
  };

  // Build a lookup map from personalization data: lesson_number -> priority info
  const priorityMap = useMemo(() => {
    if (!personalizedData) return {};
    const map = {};
    personalizedData.forEach((item, idx) => {
      map[item.lesson_number] = { ...item.priority, rank: idx + 1 };
    });
    return map;
  }, [personalizedData]);

  // Process nodes for display (reverse for bottom-to-top layout)
  const displayNodes = useMemo(() => {
    if (!pathData?.nodes) return [];
    const nonCastle = pathData.nodes.filter(n => !n.is_castle);
    
    if (personalizedMode && personalizedData?.length) {
      // Reorder nodes by personalization priority (highest score first)
      const orderMap = {};
      personalizedData.forEach((item, idx) => {
        orderMap[item.lesson_number] = idx;
      });
      const sorted = [...nonCastle].sort((a, b) => {
        const aIdx = orderMap[a.lesson_number] ?? 999;
        const bIdx = orderMap[b.lesson_number] ?? 999;
        return aIdx - bIdx;
      });
      return sorted.reverse(); // reverse for bottom-to-top
    }
    
    return [...nonCastle].reverse();
  }, [pathData, personalizedMode, personalizedData]);

  // Calculate path height based on number of nodes - Duolingo-style compact spacing
  const pathHeight = useMemo(() => {
    const nodeCount = displayNodes.length;
    const nodeSpacing = 100; // Duolingo uses ~100px spacing for mobile
    const baseHeight = 280; // Castle + entrance area (more compact)
    return baseHeight + (nodeCount * nodeSpacing);
  }, [displayNodes]);

  // Torch positions
  const torchPositions = useMemo(() => {
    return [
      { top: 150, position: 'left', delay: 0 },
      { top: 150, position: 'right', delay: 0.3 },
      { top: 550, position: 'left', delay: 0.5 },
      { top: 550, position: 'right', delay: 0.8 },
      { top: 950, position: 'left', delay: 0.2 },
      { top: 950, position: 'right', delay: 0.6 },
      { top: 1350, position: 'left', delay: 0.4 },
      { top: 1350, position: 'right', delay: 0.9 },
      { top: 1750, position: 'left', delay: 0.1 },
      { top: 1750, position: 'right', delay: 0.7 },
    ];
  }, []);

  // Handlers
  const handleCourseChange = (newCourse) => {
    if (pathData?.course_status) {
      const status = pathData.course_status.find(s => s.course === newCourse);
      if (status && !status.is_unlocked) {
        toast.error(`Complete Course ${newCourse - 1} to unlock this course`);
        return;
      }
    }
    navigate(`/forge/${languageCode}?course=${newCourse}`);
  };

  const handleNodeClick = (node) => {
    if (node.state === 'locked') {
      toast.error('Complete the previous lesson first!');
      return;
    }
    // Attach personalization data if available
    const priority = priorityMap[node.lesson_number] || null;
    setSelectedLesson({ ...node, priority });
  };

  const handleStartLesson = (lessonNumber) => {
    setSelectedLesson(null);
    navigate(`/forge/${languageCode}/lesson/${course}/${lessonNumber}`);
  };

  const handleCastleClick = () => {
    const castleNode = pathData?.nodes?.find(n => n.lesson_number === 25);
    if (castleNode && castleNode.state !== 'locked') {
      handleStartLesson(25);
    } else {
      toast.info('Complete all lessons to unlock The Reforge!');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto" />
          <p className="mt-4 text-slate-400">{t('forge.loadingPath')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !pathData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
            <span className="text-3xl">🔒</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            {error || t('forge.failedToLoad')}
          </h2>
          <p className="text-slate-400 mb-4">
            {course > 1 ? t('forge.completePreviousCourse', { course: course - 1 }) : t('common.error')}
          </p>
          <button 
            onClick={() => course > 1 ? navigate(`/forge/${languageCode}?course=${course - 1}`) : fetchPathData()}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg transition-colors"
          >
            {course > 1 ? t('forge.goToCourse', { course: course - 1 }) : t('forge.retry')}
          </button>
        </div>
      </div>
    );
  }

  const { blacksmith, themes, course_status } = pathData;
  const castleNode = pathData.nodes?.find(n => n.lesson_number === 25);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="forge-path">
      {/* Fixed Header */}
      <ForgeHeader
        courseName={pathData.course_name}
        wordRange={`${(course - 1) * 500 + 1}-${course * 500}`}
        blacksmith={blacksmith}
        totalGoldLessons={blacksmith?.total_gold_lessons || 0}
        nextRankAt={blacksmith?.next_rank_at || 10}
        totalScore={blacksmith?.total_score || 0}
      />

      {/* Course Selector */}
      <CourseSelector
        courses={[1, 2, 3, 4]}
        activeCourse={course}
        courseStatus={course_status}
        onCourseChange={handleCourseChange}
      />

      {/* Personalized Mode Toggle + Stories Link */}
      <div className="bg-slate-900/70 backdrop-blur-sm border-b border-slate-800/50 px-4 py-2" data-testid="personalized-toggle-bar">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-3">
          {personalizedData?.length > 0 && (
            <button
              onClick={togglePersonalizedMode}
              className={`
                flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-300
                ${personalizedMode 
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-600/30 border border-purple-400/40' 
                  : 'bg-slate-800/80 text-slate-400 border border-slate-700/60 hover:border-purple-500/40 hover:text-purple-300'}
              `}
              data-testid="personalized-mode-toggle"
            >
              {personalizedMode ? <Compass size={14} /> : <List size={14} />}
              {personalizedMode ? 'Personalized Order' : 'Standard Order'}
            </button>
          )}
          {personalizedMode && personalizedData?.length > 0 && (
            <span className="text-[10px] text-purple-300/60">
              Lessons reordered by your weakness
            </span>
          )}
          <button
            onClick={() => navigate(`/forge-stories/${languageCode}?course=${course}`)}
            className="flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-slate-800/80 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-950/40 hover:border-indigo-400/50 transition-all duration-300"
            data-testid="forge-stories-btn"
          >
            <BookOpen size={14} /> Stories
          </button>
        </div>
      </div>

      {/* Main Dungeon Scene */}
      <DungeonBackground>
        <div 
          className="relative max-w-md mx-auto px-4"
          style={{ minHeight: `${pathHeight}px` }}
        >
          {/* Ember Particles - reduced count on mobile */}
          <EmberParticles count={typeof window !== 'undefined' && window.innerWidth < 768 ? 12 : 25} intensity={0.6} />

          {/* Path Content - Duolingo-style centered zigzag */}
          <div className="relative z-10">
            {/* Castle at Top */}
            <div className="flex justify-center pb-4">
              <CastleTop
                courseNumber={course}
                lessonNumber={25}
                title={course === 4 ? "The Grand Forge" : "The Reforge"}
                isUnlocked={castleNode?.state !== 'locked'}
                onClick={handleCastleClick}
              />
            </div>

            {/* Lesson Nodes - Duolingo zigzag pattern */}
            <div className="relative flex flex-col items-center">
              {displayNodes.map((node, index) => {
                const actualIndex = pathData.nodes.length - 1 - index;
                const isCurrentLesson = node.is_current;
                const nextNode = displayNodes[index + 1];
                
                // Duolingo-style zigzag: nodes alternate left-center-right
                // Pattern: center, left, center, right, center, left...
                const positionPattern = ['center', 'left', 'center', 'right'];
                const position = positionPattern[index % 4];
                
                // Calculate horizontal offset for zigzag
                const getOffset = () => {
                  switch (position) {
                    case 'left': return '-35%';
                    case 'right': return '35%';
                    default: return '0';
                  }
                };

                return (
                  <React.Fragment key={node.lesson_number}>
                    {/* Node - positioned in zigzag */}
                    <div
                      className="relative flex justify-center w-full"
                      style={{ 
                        transform: `translateX(${getOffset()})`,
                        marginBottom: '24px'
                      }}
                    >
                      <LessonNode
                        lessonNumber={node.lesson_number}
                        title={node.title}
                        theme={node.theme}
                        state={node.state}
                        isCurrent={isCurrentLesson}
                        levelsCompleted={node.levels_completed}
                        currentLevelSteps={node.current_level_steps || 0}
                        decayLevel={node.decay_level || 0}
                        blacksmithImage={isCurrentLesson ? blacksmith?.image_url : null}
                        onClick={() => handleNodeClick(node)}
                        position={position === 'left' ? 'left' : position === 'right' ? 'right' : 'center'}
                        scale={1.3}
                        priority={personalizedMode ? priorityMap[node.lesson_number] : null}
                      />
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Forge Entrance */}
            <ForgeEntrance 
              onClick={() => {
                const firstNode = pathData.nodes?.find(n => n.lesson_number === 1);
                if (firstNode) handleNodeClick(firstNode);
              }}
            />
          </div>
        </div>
      </DungeonBackground>

      {/* Lesson Details Modal */}
      <LessonModal
        isOpen={!!selectedLesson}
        onClose={() => setSelectedLesson(null)}
        lesson={selectedLesson}
        onStartLesson={handleStartLesson}
      />

      {/* Onboarding Tutorial for New Users */}
      {showOnboarding && (
        <ForgeOnboarding 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
          languageCode={languageCode}
        />
      )}

      {/* Global Styles for Animations */}
      <style>{`
        @keyframes flicker {
          0%, 100% { 
            opacity: 1; 
            transform: scale(1) rotate(0deg); 
          }
          25% { 
            opacity: 0.9; 
            transform: scale(0.97) rotate(-2deg); 
          }
          50% { 
            opacity: 0.85; 
            transform: scale(0.95) rotate(1deg); 
          }
          75% { 
            opacity: 0.95; 
            transform: scale(0.98) rotate(-1deg); 
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
      `}</style>
    </div>
  );
}
